const languageSettingsContent = {
  title: "Language Settings",
  description: "Switch Sortio's interface between 6 languages and control the language the AI uses for folder names and sort feedback.",
  sections: [
    {
      id: "overview",
      title: "Two Settings, Two Jobs",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          As of v8.17.0, Sortio is fully localized. Open <strong>Settings &gt; Language</strong> and
          you'll find two independent settings:
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Interface language</h4>
            <p class="text-[#424245] text-sm">The language of the app itself: buttons, menus, labels, notifications. Six languages are available.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Content language</h4>
            <p class="text-[#424245] text-sm">The language the AI uses for what it writes: new folder names and sort feedback. Follows your system language by default.</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          They're separate on purpose. You can run the app in English while having sorts produce Korean
          folder names, or use the Chinese interface with English output for a shared drive. Mix and
          match however your work requires.
        </p>
      `
    },
    {
      id: "interface-language",
      title: "Interface Language",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Pick your interface language from the grid in <strong>Settings &gt; Language</strong>. The
          change applies immediately across the app. Sortio ships in six languages:
        </p>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-4 bg-white/80 backdrop-blur text-center">
            <p class="font-semibold text-[#1d1d1f]">English</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-4 bg-white/80 backdrop-blur text-center">
            <p class="font-semibold text-[#1d1d1f]">简体中文</p>
            <p class="text-[#86868b] text-xs mt-1">Chinese (Simplified)</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-4 bg-white/80 backdrop-blur text-center">
            <p class="font-semibold text-[#1d1d1f]">繁體中文</p>
            <p class="text-[#86868b] text-xs mt-1">Chinese (Traditional)</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-4 bg-white/80 backdrop-blur text-center">
            <p class="font-semibold text-[#1d1d1f]">한국어</p>
            <p class="text-[#86868b] text-xs mt-1">Korean</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-4 bg-white/80 backdrop-blur text-center">
            <p class="font-semibold text-[#1d1d1f]">Deutsch</p>
            <p class="text-[#86868b] text-xs mt-1">German</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-4 bg-white/80 backdrop-blur text-center">
            <p class="font-semibold text-[#1d1d1f]">Español</p>
            <p class="text-[#86868b] text-xs mt-1">Spanish</p>
          </div>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">ℹ️</span>
            One restart note
          </h4>
          <p class="text-[#424245] text-sm">A few OS-level surfaces, like the menu bar and tray, pick up the new language after you restart the app. Everything inside the window switches right away.</p>
        </div>
      `
    },
    {
      id: "content-language",
      title: "Content Language",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          The <strong>Content language</strong> dropdown controls the language the AI writes in when it
          creates output for you: the names of new folders a sort creates and the feedback it gives about
          a sort plan.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Default: System default</h4>
              <p class="text-[#424245]">Out of the box, the content language follows your operating system's language. If your Mac runs in Korean, sorts produce Korean folder names without any setup.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Or pick one explicitly</h4>
              <p class="text-[#424245]">The dropdown offers 20 languages, including English, Chinese, Korean, Japanese, German, Spanish, French, Portuguese, Italian, Dutch, Russian, and Arabic. Pick one and every AI sort writes folder names in that language, regardless of your system or interface language.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Wider than the interface list</h4>
              <p class="text-[#424245]">Content language is not limited to the six interface languages. You can run the English UI and have the AI name folders in Japanese or French.</p>
            </div>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          Prompts work in your language too: you can write sort prompts in Chinese or Korean (or any
          language you're comfortable in) and Sortio will follow them.
        </p>
      `
    },
    {
      id: "examples",
      title: "Example Setups",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Bilingual archive</h4>
              <p class="text-[#424245]">Korean interface, English content language. You work in Korean, but the sorted folder tree stays in English so colleagues abroad can navigate it.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Local-language workspace</h4>
              <p class="text-[#424245]">Chinese (Simplified) interface with Chinese content. Both the app and the folder names it creates match the rest of your environment.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">English app, native output</h4>
              <p class="text-[#424245]">English interface with German content language: app screenshots and support docs match the English documentation, but client folders get German names.</p>
            </div>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          If sorts are producing folder names in a language you didn't expect, check the content language
          first: on "System default" it follows your OS language, which may differ from the interface
          language you chose.
        </p>
      `
    }
  ]
};

export default languageSettingsContent;
